import { inject } from '@angular/core';
import { CanActivateFn, Router, ActivatedRouteSnapshot, RouterStateSnapshot } from '@angular/router';
import { SecurityService } from '../services/security.service';
import { InactivityMonitorService } from '../services/inactivity-monitor.service';
import { PinValidationService } from '../services/pin-validation.service';
import { map, catchError } from 'rxjs/operators';
import { of } from 'rxjs';
import { HttpErrorResponse } from '@angular/common/http';
import { SessionStoreService } from '../services/session-store.service';

/**
 * AuthGuard - Protege rutas verificando autenticación y actividad
 * 
 * Responsabilidades (Single Responsibility Principle):
 * 1. Verificar existencia de tokens
 * 2. Verificar inactividad del usuario
 * 3. Renovar JWT si está expirado
 * 4. Redirigir según estado de autenticación
 * 
 * Principios aplicados:
 * - SRP: Una sola responsabilidad (protección de rutas)
 * - DIP: Depende de abstracciones (servicios inyectados)
 * - Clean Code: Lógica clara y secuencial
 */
export const AuthGuard: CanActivateFn = (
    route: ActivatedRouteSnapshot,
    state: RouterStateSnapshot
) => {
  const router = inject(Router);
  const authService = inject(SecurityService);
  const inactivityMonitor = inject(InactivityMonitorService);
  const pinValidationService = inject(PinValidationService);
  const sessionStore = inject(SessionStoreService);

  const isBlockedByInactivity = (): boolean => {
    if (!inactivityMonitor.shouldRequestPinForInactivity()) return false;

    if (!pinValidationService.isPinModalVisible()) {
      authService.isPinValidated(false);
      pinValidationService.showPinModal();
    }
    return true;
  };

  const needsAdminPortalSelection = (): boolean => {
    if (!authService.isSuperAdmin()) return false;

    const selectedSession = sessionStore.getSesion();
    const partner = selectedSession?.partner;
    const hasValidPartner = (typeof partner === 'number' && Number.isInteger(partner) && partner > 0) ||
      (typeof partner === 'string' && /^[1-9]\d*$/.test(partner.trim()));
    if (hasValidPartner) return false;

    if (selectedSession !== null) {
      sessionStore.clear();
    }

    router.navigate(['/validate']);
    return true;
  };

  const canUseSession = (): boolean => {
    if (isBlockedByInactivity()) return false;
    if (!authService.getPinValidationStatus()) {
      router.navigate(['/validate']);
      return false;
    }
    return !needsAdminPortalSelection();
  };

  const handleRefreshFailure = (error: unknown) => {
    if (error instanceof HttpErrorResponse &&
        error.status === 403 &&
        error.error?.error === 'MFA_REQUIRED') {
      authService.isPinValidated(false);
      if (!pinValidationService.isPinModalVisible()) {
        pinValidationService.showPinModal();
      }
      return of(false);
    }

    authService.logout();
    return of(false);
  };

  const rejectMissingJwt = (): false => {
    authService.logout();
    return false;
  };

  const token = authService.getToken();
  const refreshToken = authService.getRefreshToken();

  // 1. Verificar que existan tokens
  if (!token && !refreshToken) {
    // Posible F5 con cookie HttpOnly viva: intentar restaurar sesión
    return authService.refreshToken().pipe(
      map((response: any) => {
        if (response && response.jwt) {
          return canUseSession();
        }
        return rejectMissingJwt();
      }),
      catchError(handleRefreshFailure)
    );
  }

  // 2. Verificar inactividad (nueva pestaña o regreso después de tiempo)
  if (isBlockedByInactivity()) return false;

  // 3. Verificar que el PIN esté validado
  const isPinValidated = authService.getPinValidationStatus();
  if (!isPinValidated) {
    router.navigate(['/validate']);
    return false;
  }

  if (needsAdminPortalSelection()) return false;

  // 4. Si el token no está expirado, permitir acceso
  if (!authService.isTokenExpired()) {
    return true;
  }

  // 5. Token expirado, intentar renovar con refresh token
  return authService.refreshToken().pipe(
    map((response: any) => {
      if (response && response.jwt) {
        if (isBlockedByInactivity()) return false;
        if (!authService.getPinValidationStatus()) {
          // Solo mostrar modal si no está ya visible
          if (!pinValidationService.isPinModalVisible()) {
            authService.isPinValidated(false);
            pinValidationService.showPinModal();
          }
          return false;
        }
        return !needsAdminPortalSelection();
      }
      return rejectMissingJwt();
    }),
    catchError(handleRefreshFailure)
  );
};
